import { useQuery } from "@tanstack/react-query";
import axiosInstance from "../../utils/axiosConfig";

import { API_URL } from "../../constant";
import { useAuth } from "../../context/auth-context";
import { apiQueryKey } from "./queryKeys";

export const FAILED_PAYOUTS_PAGE_SIZE = 20;

export type FailedPayoutsPageMeta = {
  totalPages: number;
  currentPage: number;
  totalPayouts: number;
};

export type FailedPayoutsPageData = {
  payouts: Record<string, unknown>[];
  meta: FailedPayoutsPageMeta;
};

/**
 * GET api/payouts?status=FAILED&page&limit — paginated failed payouts for resolve UI.
 */
export function useFailedPayoutsPageQuery(page: number, enabled: boolean) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: apiQueryKey("admin-payouts-failed", {
      page,
      limit: FAILED_PAYOUTS_PAGE_SIZE,
    }),
    queryFn: async ({ signal }): Promise<FailedPayoutsPageData> => {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(FAILED_PAYOUTS_PAGE_SIZE),
        status: "FAILED",
      });
      const { data } = await axiosInstance.get<{
        data?: {
          payouts?: Record<string, unknown>[];
          meta?: {
            totalPages?: number;
            currentPage?: number;
            totalPayouts?: number;
          };
        };
      }>(`${API_URL}api/payouts?${params}`, {
        signal,
      });
      const inner = data?.data;
      const payouts = inner?.payouts ?? [];
      const totalPages = Math.max(1, Number(inner?.meta?.totalPages) || 1);
      const currentPage = Number(inner?.meta?.currentPage) || page;
      const totalPayouts = Number(inner?.meta?.totalPayouts) ?? payouts.length;
      return {
        payouts,
        meta: { totalPages, currentPage, totalPayouts },
      };
    },
    enabled: Boolean(isAuthenticated) && enabled,
    staleTime: 30_000,
  });
}
